import React from 'react';

/**
 * Writing Types
 * Types for the AI-powered writing practice journey
 *
 * Note: Interface names retain Quest* prefix for backward compat with
 * data structures and DB schema. User-facing strings are 'Writing'.
 */

// Gate types
export type GateType = 'translation' | 'combine' | 'expansion' | 'essay';

// Gate status
export type GateStatus = 'locked' | 'available' | 'in_progress' | 'completed';

// Question types for each gate
export interface TranslationQuestion {
  id: string;
  sourceText: string;      // Chinese sentence to translate
  referenceAnswer: string; // Reference English translation
  keywords?: string[];     // Key vocabulary to use
  hint?: string;           // Hint for the translation
}

export interface CombineQuestion {
  id: string;
  sentences: string[];     // 2-3 sentences to combine
  relationship: 'causal' | 'contrast' | 'addition' | 'temporal' | 'conditional';
  suggestedConnectors: string[];
  translation?: string;
}

export interface ExpansionQuestion {
  id: string;
  seedSentence: string;    // Starting sentence
  requiredVocabulary: string[];  // Words that must be used
  minSentences: number;    // Minimum sentences required
  theme?: string;          // Optional theme/direction
  translation?: string;
}

export interface EssayQuestion {
  id: string;
  topic: string;           // Essay topic
  instructions: string;    // Writing instructions
  minWords: number;        // Minimum word count
  maxWords: number;        // Maximum word count
  keyPoints?: string[];    // Points to address
  translation?: string;
}

// Evaluation result from AI
export interface GateEvaluationResult {
  score: number;           // 0-100
  passed: boolean;         // score >= 60
  feedback: string;        // Traditional Chinese feedback
  strengths?: string[];
  grammarIssues?: string[];
  suggestions?: string[];
  improvedVersion?: string;
  encouragement?: string;
  tokensEarned?: number;
  xpEarned?: number;
}

// Knowledge point weakness
export interface KnowledgePointWeakness {
  category: 'grammar' | 'vocabulary' | 'connector' | 'structure' | 'spelling' | 'punctuation';
  specificPoint: string;
  specificPointChinese: string;
  description: string;
  evidence: string;
  correction: string;
  difficulty: 'basic' | 'intermediate' | 'advanced';
}

// Diagnostic report (generated after completing all gates)
export interface DiagnosticReport {
  overallScore: number;
  knowledgePointAnalysis: {
    weakPoints: KnowledgePointWeakness[];
    strongPoints: string[];
  };
  overallDiagnosis: string;
  prioritizedFixes: string[];
  recommendedLessons: string[];
  progressSummary: {
    vocabularyMastery: number;
    grammarAccuracy: number;
    writingFluency: number;
    connectorUsage: number;
  };
}

// User's writing session progress (per-course)
export interface QuestProgress {
  id: string;
  userId: string;
  courseId: string;
  currentGate: GateType;
  gateProgress: {
    translation: GateStatus;
    combine: GateStatus;
    expansion: GateStatus;
    essay: GateStatus;
  };
  scores: {
    translation?: number;
    combine?: number;
    expansion?: number;
    essay?: number;
  };
  bestScores?: {
    translation?: number;
    combine?: number;
    expansion?: number;
    essay?: number;
  };
  totalXp: number;
  tokensSpent: number;
  completedAt?: string;
  diagnosticReport?: DiagnosticReport;
  lastPlayedAt: string;
  createdAt: string;
}

// Alias (user-facing naming) - identical shape
export type WritingProgress = QuestProgress;
export type WritingSession = QuestProgress;

// Gate review data for viewing completed writing results
export interface GateReviewData {
  questions: TranslationQuestion[] | CombineQuestion[] | ExpansionQuestion[] | EssayQuestion[];
  answers: string[];
  score: number;
  isPassed: boolean;
  aiFeedback: {
    overallScore: number;
    strengths: string[];
    improvements: string[];
    correctedVersion?: string;
    encouragement: string;
  } | null;
  completedAt: string;
}

// Gate configuration
export interface GateConfig {
  type: GateType;
  name: string;
  nameChinese: string;
  description: string;
  tokenCost: number;
  xpReward: number;
  passingScore: number;
  icon: React.ReactNode;
}

// Token costs for each gate
export const QUEST_TOKEN_COSTS: Record<GateType, number> = {
  translation: 1,
  combine: 2,
  expansion: 3,
  essay: 5,
};

export const WRITING_TOKEN_COSTS = QUEST_TOKEN_COSTS;

// XP rewards for each gate (on passing)
export const QUEST_XP_REWARDS: Record<GateType, number> = {
  translation: 10,
  combine: 20,
  expansion: 30,
  essay: 50,
};

export const WRITING_XP_REWARDS = QUEST_XP_REWARDS;

// Gate configurations
export const GATE_CONFIGS: GateConfig[] = [
  {
    type: 'translation',
    name: 'Translation',
    nameChinese: '中翻英',
    description: 'Translate Chinese sentences into English',
    tokenCost: 1,
    xpReward: 10,
    passingScore: 60,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 8l4 8" />
        <path d="M9 8l4 8" />
        <path d="M6.5 13h5" />
        <path d="M14 4l6 0" />
        <path d="M17 4v4" />
        <path d="M20 8c-2 2-4 3.5-7 4" />
        <path d="M14 8c1 2 3 3.5 6 4" />
      </svg>
    ),
  },
  {
    type: 'combine',
    name: 'Sentence Combining',
    nameChinese: '造句',
    description: 'Combine multiple sentences using appropriate connectors',
    tokenCost: 2,
    xpReward: 20,
    passingScore: 60,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 6l-4 6 4 6" />
        <path d="M16 6l4 6-4 6" />
        <path d="M12 6v12" />
      </svg>
    ),
  },
  {
    type: 'expansion',
    name: 'Paragraph Expansion',
    nameChinese: '擴寫',
    description: 'Expand a seed sentence into a full paragraph',
    tokenCost: 3,
    xpReward: 30,
    passingScore: 60,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3l6 6-6 6" />
        <path d="M9 21l-6-6 6-6" />
        <path d="M21 9H3" />
        <path d="M21 15H3" />
      </svg>
    ),
  },
  {
    type: 'essay',
    name: 'Essay Writing',
    nameChinese: '作文',
    description: 'Write a complete essay on a given topic',
    tokenCost: 5,
    xpReward: 50,
    passingScore: 60,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M16 13l-3.5 3.5L11 15" />
        <line x1="8" y1="13" x2="8" y2="13" />
        <line x1="8" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
];

// API Response types (wrappers)
export interface QuestProgressResponse {
  success: boolean;
  data: QuestProgress;
}

export type WritingProgressResponse = QuestProgressResponse;

export interface QuestQuestionsResponse {
  success: boolean;
  data: {
    gateType: GateType;
    questions: TranslationQuestion[] | CombineQuestion[] | ExpansionQuestion[] | EssayQuestion[];
  };
}

export type WritingQuestionsResponse = QuestQuestionsResponse;

export interface QuestEvaluationResponse {
  success: boolean;
  data: GateEvaluationResult;
}

export type WritingEvaluationResponse = QuestEvaluationResponse;

export interface QuestDiagnosticResponse {
  success: boolean;
  data: DiagnosticReport;
}

export type WritingDiagnosticResponse = QuestDiagnosticResponse;

// Leaderboard types
export interface LeaderboardEntry {
  rank: number;
  userId: string;
  username: string;
  avatarUrl?: string;
  totalXp: number;
  questsCompleted: number;
  averageScore: number;
  badges: string[];
}

export interface LeaderboardResponse {
  success: boolean;
  data: {
    entries: LeaderboardEntry[];
    userRank?: LeaderboardEntry;
    period: 'daily' | 'weekly' | 'monthly' | 'all-time';
  };
}

// ============================================
// STANDALONE WRITING TYPES
// ============================================

// Daily challenge status
export interface DailyStatus {
  date: string;
  questsCompleted: number;
  rewardClaimed: boolean;
  totalXpToday: number;
  streakBonusXp: number;
  currentStreak: number;
  longestStreak: number;
  hasActiveQuest: boolean;
  canEarnDailyReward: boolean;
  streakMultiplier: number;
}

// Streak information
export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  totalActiveDays: number;
  lastActiveDate: string | null;
  streakStartDate: string | null;
  totalStreakBonusXp: number;
  nextMilestone: number;
  daysToMilestone: number;
  multiplier: number;
}

// Vocabulary source option
export interface VocabularySourceOption {
  id: string;
  name: string;
  nameEn: string;
  count: number;
  description: string;
  recommended?: boolean;
}

// Course with vocabulary count
export interface CourseVocabOption {
  id: string;
  name: string;
  count: number;
}

// Vocabulary sources response
export interface VocabularySourcesResponse {
  sources: VocabularySourceOption[];
  courses: CourseVocabOption[];
  minimumRequired: number;
  hasEnoughVocabulary: boolean;
}

// DTO for starting standalone writing session
export type VocabularySource = 'all' | 'weak' | 'course' | 'due' | 'random';
export type QuestContentType = 'vocabulary' | 'patterns' | 'mixed';
export type WritingContentType = QuestContentType;

export interface StartStandaloneQuestDto {
  vocabularySource?: VocabularySource;
  courseId?: string;
  contentType?: QuestContentType;
  vocabularyCount?: number;
  patternCount?: number;
}

export type StartStandaloneWritingDto = StartStandaloneQuestDto;

// Standalone writing session progress
export interface StandaloneQuestProgress {
  id: string;
  userId: string;
  type: string;
  vocabularySource: VocabularySource;
  contentType: QuestContentType;
  currentGate: GateType;
  gateProgress: {
    translation: GateStatus;
    combine: GateStatus;
    expansion: GateStatus;
    essay: GateStatus;
  };
  vocabularyCount: number;
  patternCount: number;
  scores: {
    translation?: number;
    combine?: number;
    expansion?: number;
    essay?: number;
  };
  totalXp: number;
  tokensSpent: number;
  completedAt?: string;
  createdAt: string;
}

export type StandaloneWritingProgress = StandaloneQuestProgress;

// Badge types
export interface Badge {
  id: string;
  name: string;
  nameChinese: string;
  description: string;
  icon: string;
  requirement: string;
  earnedAt?: string;
}

// ============================================
// VOCABULARY USAGE ANALYSIS TYPES
// ============================================

// Individual vocabulary usage in answer
export interface VocabularyUsageItem {
  word: string;                   // The vocabulary word
  wordId?: string;                // Vocabulary ID (if exists in DB)
  usedCorrectly: boolean;         // Whether used without errors
  wasRequired: boolean;           // Whether this was a required word
  isExistingInUserData: boolean;  // Whether already in user's vocabulary
  currentFamiliarity: number;     // Current familiarity score (0-100)
  usageContext?: string;          // The sentence where word was used
  errorType?: 'spelling' | 'grammar' | 'context' | null;
  errorDetail?: string;           // Description of the error
  correction?: string;            // Corrected usage
}

// Familiarity change for a word
export interface FamiliarityChangeItem {
  word: string;
  wordId?: string;
  previousFamiliarity: number;
  newFamiliarity: number;
  change: number;                 // +1, +2, or +4
  reason: 'correct_existing' | 'error_existing' | 'correct_new' | 'error_new';
  isNew: boolean;
  encouragement?: string;         // Encouragement message for low-familiarity words used correctly
}

// Complete vocabulary analysis in evaluation result
export interface VocabularyAnalysis {
  usedVocabulary: VocabularyUsageItem[];
  requiredVocabulary: string[];
  requiredVocabularyUsed: string[];
  requiredVocabularyMissed: string[];
  familiarityChanges: FamiliarityChangeItem[];
  totalFamiliarityGain: number;
  encouragementMessage?: string;  // Special encouragement for using difficult words
  challengeWords?: string[];      // Suggested advanced synonyms for next time
}

// Extended evaluation result with vocabulary analysis
export interface ExtendedGateEvaluationResult extends GateEvaluationResult {
  vocabularyAnalysis?: VocabularyAnalysis;
}

// User learning data (loaded on writing page entry)
export interface UserLearningData {
  vocabulary: UserVocabularyItem[];
  knowledgePoints: UserKnowledgePointItem[];
  abilityLevel: {
    overall: number;
    vocabularyMastery: number;
    grammarAccuracy: number;
    writingFluency: number;
  };
  cefrLevel: string;
}

export interface UserVocabularyItem {
  id: string;
  vocabularyId: string;
  word: string;
  definition?: string;
  masteryLevel: number;           // 0-5
  familiarity: number;            // 0-100 (computed from masteryLevel)
  lastReviewedAt?: string;
  state: 'new' | 'learning' | 'review' | 'relearning';
}

export interface UserKnowledgePointItem {
  id: string;
  knowledgePointId: string;
  term: string;
  category: 'vocabulary' | 'phrasal_verb' | 'grammar';
  cefrLevel: string;
  masteryLevel: number;
  familiarity: number;
}
